import React, { useState, useEffect, useRef, Fragment, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, Transition, Combobox } from '@headlessui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { db } from '../config/firebase';
import { hasPageAccessSync, hasFeatureAccessSync } from '../utils/accessControl';
import { 
  collection, 
  query, 
  where, 
  onSnapshot,
  getDocs,
  orderBy,
  limit,
  startAt,
  endAt,
  or,
  writeBatch,
  doc,
  getDoc
} from 'firebase/firestore';
import { useTheme } from '../contexts/ThemeContext';
import { useSidebar } from '../contexts/SidebarContext';
import { useAuth } from '../contexts/AuthContext';
import { useLogo } from '../contexts/LogoContext';
import { 
  FaSearch, 
  FaCog, 
  FaUserCircle, 
  FaSignOutAlt,
  FaSun,
  FaMoon,
  FaBars,
  FaChevronDown,
  FaUser,
  FaUserTie,
  FaBuilding,
  FaUsers,
  FaTools,
  FaPoundSign,
  FaChevronRight,
  FaShieldAlt,
  FaBell,
  FaFileDownload,
  FaFilter,
  FaCalendar,
  FaEnvelope,
  FaCertificate,
  FaCreditCard,
  FaHome,
  FaChartLine,
  FaArchive,
  FaLink,
  FaBullhorn,
  FaComments,
  FaClipboardCheck,
} from 'react-icons/fa';
import NewCustomButton from './common/NewCustomButton';
import { isFeatureEnabled } from '../utils/featureFlags';
import { FEATURES } from '../features';
import '../styles/TopBar.css';

const TopBar = () => {
  const navigate = useNavigate();
  const [notificationCount, setNotificationCount] = useState(0);
  const { currentUser, logout, refreshUser, userRole, userPermissions } = useAuth();
  const { isCollapsed, isMobileOpen, toggleSidebar } = useSidebar();
  const { isDarkMode, toggleTheme, primaryColor, secondaryColor } = useTheme();
  const { logoUrl } = useLogo();
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const searchInputRef = useRef(null);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const searchModalRef = useRef(null);
  const modalRef = useRef(null);
  const searchButtonRef = useRef(null);
  const searchRequestIdRef = useRef(0);
  const [localUserRole, setLocalUserRole] = useState(null);
  
  // Helper function to check page access using permissions from AuthContext
  const checkPageAccess = useCallback((pageName) => {
    if (!userRole || !pageName) return false;
    
    // Administrators and owners always have access
    const normalizedRole = userRole.toLowerCase();
    if (normalizedRole === 'administrator' || normalizedRole === 'owner') return true;
    
    // Check user permissions from context
    if (userPermissions?.pages && pageName in userPermissions.pages) {
      return userPermissions.pages[pageName] === true;
    }
    
    return false;
  }, [userRole, userPermissions]);

  // Helper function to check feature access using permissions from AuthContext
  const checkFeatureAccess = useCallback((featureName) => {
    if (!userRole || !featureName) return false;
    
    // Administrators and owners always have access
    const normalizedRole = userRole.toLowerCase();
    if (normalizedRole === 'administrator' || normalizedRole === 'owner') return true;
    
    // Check user permissions from context
    if (userPermissions?.features && featureName in userPermissions.features) {
      return userPermissions.features[featureName] === true;
    }
    
    return false;
  }, [userRole, userPermissions]);

  // Helper to check TopBar link visibility
  const checkTopBarLinkAccess = useCallback((linkId) => {
    if (!userRole || !linkId) return false;
    const normalizedRole = userRole.toLowerCase();
    if (normalizedRole === 'administrator' || normalizedRole === 'owner') return true;
    return userPermissions?.topBarLinks?.[linkId] === true;
  }, [userRole, userPermissions]);
  const [hasRefreshed, setHasRefreshed] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(0);
  const [welcomeMessage, setWelcomeMessage] = useState('Nice to see you again');
  const [currentDate, setCurrentDate] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  /** Top bar search pill: same scale as NewCustomButton compact + theme colors */
  const searchPillStyle = useMemo(() => {
    const normalizeHex = (c, fallback) => {
      if (!c || typeof c !== 'string') return fallback;
      let h = c.trim().replace(/^#/, '');
      if (h.length === 3) h = h.split('').map((x) => x + x).join('');
      if (!/^[0-9A-Fa-f]{6}$/.test(h)) return fallback;
      return `#${h}`;
    };
    const pri = normalizeHex(primaryColor, '#4d8063');
    const textCol = normalizeHex(secondaryColor, isDarkMode ? '#E5E7EB' : '#1F1F1F');
    const r = parseInt(pri.slice(1, 3), 16) / 255;
    const g = parseInt(pri.slice(3, 5), 16) / 255;
    const b = parseInt(pri.slice(5, 7), 16) / 255;
    const L = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    const iconFg = L > 0.55 ? '#1a1a1a' : '#ffffff';
    return {
      '--search-pill-bg': `${pri}27`,
      '--search-pill-bg-hover': `${pri}49`,
      '--search-pill-primary': pri,
      '--search-pill-text': textCol,
      '--search-pill-icon-fg': iconFg,
    };
  }, [primaryColor, secondaryColor, isDarkMode]);

  // Convert hex color to RGB for opacity transformations
  const hexToRgb = (hex) => {
    // Remove the # if present
    hex = hex.replace('#', '');
    
    // Parse the hex values
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    // Return as RGB object
    return { r, g, b };
  };

  // Helper function to get user's first letter for avatar
  const getUserInitial = () => {
    if (!currentUser) return 'U';
    
    return (
      currentUser.displayName?.charAt(0).toUpperCase() || 
      currentUser.firstName?.charAt(0).toUpperCase() || 
      currentUser.lastName?.charAt(0).toUpperCase() || 
      currentUser.email?.charAt(0).toUpperCase() || 
      'U'
    );
  };

  // Categories for search
  const searchCategories = [
    { id: 'pages', name: 'Pages', icon: FaHome },
    { id: 'properties', name: 'Properties', icon: FaBuilding },
    { id: 'tenants', name: 'Tenants', icon: FaUsers },
    { id: 'maintenance', name: 'Maintenance', icon: FaTools },
    { id: 'finance', name: 'Finance', icon: FaPoundSign },
    { id: 'payments', name: 'Payments', icon: FaCreditCard },
    { id: 'landlords', name: 'Landlords', icon: FaUser },
    { id: 'users', name: 'Users', icon: FaUserTie },
    { id: 'links', name: 'Links', icon: FaChevronRight },
    { id: 'documents', name: 'Documents', icon: FaFileDownload },
    { id: 'certificates', name: 'Certificates', icon: FaCertificate },
    { id: 'calendar', name: 'Calendar', icon: FaCalendar },
    { id: 'messages', name: 'Messages', icon: FaEnvelope },
  ];

  // Define all available pages with their access requirements
  const allPages = [
    { title: 'Dashboard', path: '/dashboard', icon: FaHome, pageName: 'dashboard', requiredFeature: 'viewDashboard' },
    { title: 'Properties', path: '/properties', icon: FaBuilding, pageName: 'properties', requiredFeature: 'viewProperties' },
    { title: 'Tenants', path: '/tenants', icon: FaUsers, pageName: 'tenants', requiredFeature: 'viewTenants' },
    { title: 'Landlords', path: '/landlords', icon: FaUserTie, pageName: 'landlords', requiredFeature: 'viewLandlords' },
    { title: 'Compliance', path: '/compliance', icon: FaShieldAlt, pageName: 'properties', requiredFeature: 'viewProperties' },
    { title: 'Inspections', path: '/inspections', icon: FaClipboardCheck, pageName: 'inspections', requiredFeature: 'viewInspections' },
    { title: 'Maintenance', path: '/maintenance', icon: FaTools, pageName: 'maintenance', requiredFeature: 'viewMaintenance' },
    { title: 'Calendar', path: '/calendar', icon: FaCalendar, pageName: 'calendar', requiredFeature: 'viewCalendar' },
    { title: 'Finance', path: '/finance', icon: FaPoundSign, pageName: 'finance', requiredFeature: 'viewFinance' },
    { title: 'Reports', path: '/reports', icon: FaChartLine, pageName: 'reports', requiredFeature: 'viewReports' },
    { title: 'Links', path: '/links', icon: FaLink, pageName: 'links', requiredFeature: 'viewLinks' },
    { title: 'Company Docs', path: '/company-documents', icon: FaFileDownload, pageName: 'companyDocuments', requiredFeature: 'viewDocuments' },
    { title: 'Broadcast', path: '/broadcast', icon: FaBullhorn, pageName: 'broadcast', requiredFeature: 'sendBroadcasts' },
    { title: 'Messages', path: '/messages', icon: FaComments, pageName: 'messages', requiredFeature: 'sendMessages' },
    { title: 'Contractors', path: '/contractors', icon: FaTools, pageName: 'contractors', requiredFeature: 'viewContractors' },
    { title: 'Contractor messages', path: '/contractor-messages', icon: FaComments, pageName: 'contractors', requiredFeature: 'viewContractors' },
    { title: 'Archive', path: '/archive', icon: FaArchive, pageName: 'archive', requiredFeature: 'viewArchive', adminOnly: true },
    { title: 'Settings', path: '/settings', icon: FaCog, pageName: 'settings', requiredFeature: 'accessSettings' },
    { title: 'Profile', path: '/profile', icon: FaUserCircle, pageName: 'profile', requiredFeature: null },
    { title: 'Account Security', path: '/account-management', icon: FaShieldAlt, pageName: 'account', requiredFeature: null },
    { title: 'Notifications', path: '/notifications', icon: FaBell, pageName: 'notifications', requiredFeature: null },
  ];

  // Settings sections
  const settingsSections = [
    { title: 'Users', path: '/settings?tab=users', icon: FaUsers, pageName: 'users', requiredFeature: 'viewUsers' },
    { title: 'Roles & Permissions', path: '/settings?tab=roles', icon: FaShieldAlt, pageName: 'roles', requiredFeature: 'manageRoles' },
    { title: 'Branding', path: '/settings?tab=branding', icon: FaCog, pageName: 'branding', requiredFeature: 'manageBranding' },
    { title: 'Audit Logs', path: '/settings?tab=audit', icon: FaFileDownload, pageName: 'audit', requiredFeature: 'viewAuditLogs' },
    { title: 'System Configuration', path: '/settings?tab=system', icon: FaCog, pageName: 'system', requiredFeature: 'systemConfig' },
  ];

  useEffect(() => {
    if (!currentUser) return;

    const notificationsRef = collection(db, 'notifications');
    const notificationsQuery = query(
      notificationsRef,
      where('userId', '==', currentUser.uid),
      where('closed', '==', false),
      where('read', '==', false)
    );

    const unsubscribe = onSnapshot(notificationsQuery, (snapshot) => {
      setNotificationCount(snapshot.size);
    });

    return () => unsubscribe();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;

    // Listen for both system notifications and messages
    const conversationsRef = collection(db, 'conversations');
    const conversationsQuery = query(
      conversationsRef,
      where('participants', 'array-contains', currentUser.uid)
    );

    // Create notification for unread messages
    const handleUnreadMessages = async (conversations) => {
      const batch = writeBatch(db);
      let newNotificationsCount = 0;

      for (const convDoc of conversations.docs) {
        const conv = convDoc.data();
        if (conv.lastMessage && 
            conv.lastMessageSender !== currentUser.uid && 
            (!conv.lastRead?.[currentUser.uid] || 
             conv.lastMessageTime.seconds > conv.lastRead[currentUser.uid].seconds)) {
          
          // Check if notification already exists (not closed)
          // We check for notifications that are not closed, regardless of read status
          // This prevents recreating notifications that were marked as read
          const existingQuery = query(
            collection(db, 'notifications'),
            where('type', '==', 'NEW_MESSAGE'),
            where('conversationId', '==', convDoc.id),
            where('userId', '==', currentUser.uid),
            where('closed', '==', false)
          );
          const existing = await getDocs(existingQuery);
          
          if (existing.empty) {
            newNotificationsCount++;
            const notificationRef = doc(collection(db, 'notifications'));
            batch.set(notificationRef, {
              type: 'NEW_MESSAGE',
              userId: currentUser.uid,
              conversationId: convDoc.id,
              message: conv.lastMessage,
              timestamp: conv.lastMessageTime,
              read: false,
              closed: false,
              sender: conv.participantDetails?.[conv.lastMessageSender]?.name || 'Unknown',
              subject: conv.propertyAddress || 'General Discussion'
            });
          }
        }
      }

      if (newNotificationsCount > 0) {
        await batch.commit();
      }
    };

    const unsubscribeMessages = onSnapshot(conversationsQuery, (snapshot) => {
      handleUnreadMessages(snapshot).catch(console.error);
    });

    return () => unsubscribeMessages();
  }, [currentUser]);

  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        if (currentUser) {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setLocalUserRole(userData.role);
          }
        }
      } catch (error) {
        console.error('Error fetching user role:', error);
      }
    };

    fetchUserRole();
  }, [currentUser]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        modalRef.current && 
        searchButtonRef.current && 
        !modalRef.current.contains(event.target) && 
        !searchButtonRef.current.contains(event.target)
      ) {
        setShowSearchModal(false);
      }
    };

    if (showSearchModal) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showSearchModal]);

  // Modify the useEffect hook to prevent an infinite loop
  useEffect(() => {
    // Refresh user data to ensure we have the latest displayName
    const updateUserData = async () => {
      try {
        const now = Date.now();
        // Only refresh if it's been more than 5 minutes since the last refresh
        // or if we haven't refreshed yet
        if (currentUser && (!hasRefreshed || now - lastRefreshTime > 300000)) {
          setHasRefreshed(true);
          setLastRefreshTime(now);
          const updatedUser = await refreshUser();
          // Only log in development environment
          if (process.env.NODE_ENV !== 'production') {
            console.log("User data refreshed in TopBar:", updatedUser?.displayName || currentUser?.displayName);
          }
        }
      } catch (error) {
        console.error("Error refreshing user data in TopBar:", error);
      }
    };
    
    updateUserData();
    // Only run this effect when currentUser changes or refreshUser function changes
  }, [currentUser?.uid, refreshUser, hasRefreshed, lastRefreshTime]);

  // Add useEffect to set a random welcome message and current date
  useEffect(() => {
    const welcomeMessages = [
      'Nice to see you again',
      'Welcome back',
      'Good to have you back',
      'Hello there',
      'Great to see you',
      'Welcome'
    ];
    
    const randomMessage = welcomeMessages[Math.floor(Math.random() * welcomeMessages.length)];
    setWelcomeMessage(randomMessage);

    // Format current date
    const now = new Date();
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    const formattedDate = now.toLocaleDateString('en-US', options);
    setCurrentDate(formattedDate);
  }, []);

  // Add useEffect to handle responsive behavior for hamburger menu and search icon
  useEffect(() => {
    const checkScreenSize = () => {
      const w = window.innerWidth;
      setIsMobile(w < 768);
      setIsTablet(w >= 768 && w < 1024);
    };

    // Check on mount
    checkScreenSize();

    // Add event listener for window resize
    window.addEventListener('resize', checkScreenSize);

    // Cleanup
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const handleLogout = async () => {
    try {
      setLogoutLoading(true);
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setLogoutLoading(false);
    }
  };

  const handleNotificationsClick = () => {
    navigate('/notifications');
  };

  // Filter search results based on selected categories
  const filterResultsByCategory = (results) => {
    if (selectedCategories.length === 0) return results;
    return results.filter(result => selectedCategories.includes(result.category));
  };

  // Toggle category filter
  const toggleCategory = (categoryId) => {
    setSelectedCategories(prev => {
      if (prev.includes(categoryId)) {
        return prev.filter(id => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  };

  // Get available categories based on user role
  const getAvailableCategories = () => {
    const baseCategories = [
      { id: 'pages', name: 'Pages', icon: FaHome },
      { id: 'properties', name: 'Properties', icon: FaBuilding },
      { id: 'maintenance', name: 'Maintenance', icon: FaTools },
      { id: 'links', name: 'Links', icon: FaChevronRight },
    ];

    if (['agent', 'owner', 'administrator'].includes(userRole)) {
      baseCategories.push(
        { id: 'tenants', name: 'Tenants', icon: FaUsers },
        { id: 'landlords', name: 'Landlords', icon: FaUser },
        { id: 'documents', name: 'Documents', icon: FaFileDownload },
        { id: 'certificates', name: 'Certificates', icon: FaCertificate },
        { id: 'calendar', name: 'Calendar', icon: FaCalendar }
      );
    }

    if (['owner', 'administrator'].includes(userRole)) {
      baseCategories.push({ id: 'users', name: 'Users', icon: FaUserTie });
      if (isFeatureEnabled(FEATURES.FINANCE_MODULE)) {
        baseCategories.push(
          { id: 'finance', name: 'Finance', icon: FaPoundSign },
          { id: 'payments', name: 'Payments', icon: FaCreditCard }
        );
      }
    }

    return baseCategories;
  };

  // Perform universal search across multiple collections
  const performSearch = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }

    searchRequestIdRef.current += 1;
    const thisRequestId = searchRequestIdRef.current;
    setIsSearching(true);
    const searchResults = [];
    const lowerSearchTerm = searchTerm.toLowerCase();

    const isStale = () => thisRequestId !== searchRequestIdRef.current;

    try {
      // Search Pages - Filtered by user permissions
      const accessiblePages = allPages.filter(page => {
        if (!isFeatureEnabled(FEATURES.FINANCE_MODULE) && (page.path === '/finance' || page.path === '/reports')) {
          return false;
        }
        if (page.path === '/messages') return false;
        if (page.adminOnly) {
          const normalizedRole = userRole?.toLowerCase();
          const isAdmin = normalizedRole === 'administrator';
          const isOwner = normalizedRole === 'owner';
          if (!isAdmin && !isOwner) {
            return false;
          }
        }
        if (page.pageName && userRole) {
          const hasPage = checkPageAccess(page.pageName);
          if (!hasPage) return false;
        }
        if (page.requiredFeature && userRole) {
          const hasFeature = checkFeatureAccess(page.requiredFeature);
          if (!hasFeature) return false;
        }
        return true;
      });

      const pageMatches = accessiblePages.filter(page => {
        const matches = (
          page.title?.toLowerCase().includes(lowerSearchTerm) ||
          page.path?.toLowerCase().includes(lowerSearchTerm) ||
          page.pageName?.toLowerCase().includes(lowerSearchTerm)
        );
        return matches;
      }).slice(0, 5);

      if (pageMatches.length > 0) {
        const pageResults = {
          category: 'pages',
          icon: FaHome,
          items: pageMatches.map(page => ({
            id: page.path,
            title: page.title,
            subtitle: `Navigate to ${page.title}`,
            path: page.path,
          })),
        };
        searchResults.push(pageResults);
      }

      // Search Settings Sections - Filtered by user permissions
      if (userRole && checkPageAccess('settings')) {
        const accessibleSettings = settingsSections.filter(section => {
          if (section.requiredFeature && userRole) {
            return hasFeatureAccessSync(userRole, section.requiredFeature);
          }
          return true;
        });

        const settingsMatches = accessibleSettings.filter(section => {
          const matches = (
            section.title?.toLowerCase().includes(lowerSearchTerm) ||
            section.path?.toLowerCase().includes(lowerSearchTerm) ||
            section.pageName?.toLowerCase().includes(lowerSearchTerm)
          );
          return matches;
        }).slice(0, 5);

        if (settingsMatches.length > 0) {
          const settingsResults = {
            category: 'pages',
            icon: FaCog,
            items: settingsMatches.map(section => ({
              id: section.path,
              title: section.title,
              subtitle: 'Settings section',
              path: section.path,
            })),
          };
          searchResults.push(settingsResults);
        }
      }

      // Search Properties
      const propertiesRef = collection(db, 'properties');
      let propertiesQuery = propertiesRef;
      if (userRole === 'landlord' && currentUser) {
        propertiesQuery = query(propertiesRef, where('landlordId', '==', currentUser.uid));
      }

      const linksRef = collection(db, 'links');

      const fetchOpt = (p) => p.catch((e) => { console.warn(e?.message || e); return { docs: [] }; });
      const [propertiesSnapshot, linksSnapshot] = await Promise.all([
        fetchOpt(getDocs(propertiesQuery)),
        fetchOpt(getDocs(linksRef)),
      ]);

      const propertyMatches = propertiesSnapshot.docs.filter(doc => {
        const data = doc.data();
        const searchTerms = data.searchTerms || [];
        const matches = (
          data.address?.toLowerCase().includes(lowerSearchTerm) ||
          data.propertyType?.toLowerCase().includes(lowerSearchTerm) ||
          data.propertyName?.toLowerCase().includes(lowerSearchTerm) ||
          searchTerms.some(term => term.toLowerCase().includes(lowerSearchTerm))
        );
        return matches;
      }).slice(0, 5);

      if (propertyMatches.length > 0) {
        const propertyResults = {
          category: 'properties',
          icon: FaBuilding,
          items: propertyMatches.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              title: data.address || data.propertyName || 'Unnamed Property',
              subtitle: `${data.bedrooms || 'N/A'} bed ${data.propertyType || 'Property'}`,
            };
          }),
        };
        searchResults.push(propertyResults);
      }

      const propertyAddressById = new Map(
        propertiesSnapshot.docs.map((d) => {
          const data = d.data();
          return [d.id, data.address || data.propertyName || null];
        })
      );

      // Search Tenants - Only for agents, owners, and administrators
      let tenantsSnapshot = { docs: [] };
      let landlordsSnapshot = { docs: [] };
      if (['agent', 'owner', 'administrator'].includes(userRole)) {
        const tenantsRef = collection(db, 'tenants');
        let tenantsQuery = tenantsRef;
        if (userRole === 'agent' && currentUser) {
          tenantsQuery = query(tenantsRef, where('agentId', '==', currentUser.uid));
        }
        const landlordsQuery = query(collection(db, 'users'), where('role', '==', 'landlord'));
        const fetchOpt2 = (p) => p.catch((e) => { console.warn(e?.message || e); return { docs: [] }; });
        const [tSnap, lSnap] = await Promise.all([
          fetchOpt2(getDocs(tenantsQuery)),
          fetchOpt2(getDocs(landlordsQuery)),
        ]);
        tenantsSnapshot = tSnap;
        landlordsSnapshot = lSnap;
      }

      if (['agent', 'owner', 'administrator'].includes(userRole)) {
        const tenantMatches = tenantsSnapshot.docs.filter(doc => {
          const data = doc.data();
          const searchTerms = data.searchTerms || [];
          const matches = (
            data.firstName?.toLowerCase().includes(lowerSearchTerm) ||
            data.lastName?.toLowerCase().includes(lowerSearchTerm) ||
            data.email?.toLowerCase().includes(lowerSearchTerm) ||
            `${data.firstName} ${data.lastName}`.toLowerCase().includes(lowerSearchTerm) ||
            searchTerms.some(term => term.toLowerCase().includes(lowerSearchTerm))
          );
          return matches;
        }).slice(0, 5);

        if (tenantMatches.length > 0) {
          const tenantResults = {
            category: 'tenants',
            icon: FaUsers,
            items: tenantMatches.map(doc => {
              const data = doc.data();
              const address = data.propertyAddress || (data.propertyId ? propertyAddressById.get(data.propertyId) : null);
              return {
                id: doc.id,
                title: `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Unnamed Tenant',
                subtitle: address || 'No property assigned',
              };
            }),
          };
          searchResults.push(tenantResults);
        }
      }

      // Search Maintenance Requests
      const maintenanceRef = collection(db, 'maintenanceRequests');
      const landlordPropertyIds = (userRole === 'landlord' && propertiesSnapshot?.docs) ? propertiesSnapshot.docs.map(doc => doc.id) : [];

      if (userRole === 'landlord' && currentUser && landlordPropertyIds.length > 0) {
          const batchSize = 10;
          let allMaintenanceDocs = [];
          for (let i = 0; i < landlordPropertyIds.length; i += batchSize) {
            const batchIds = landlordPropertyIds.slice(i, i + batchSize);
            try {
              const batchQuery = query(maintenanceRef, where('propertyId', 'in', batchIds));
              const batchSnapshot = await getDocs(batchQuery);
              allMaintenanceDocs = [...allMaintenanceDocs, ...batchSnapshot.docs];
            } catch (permissionError) {
              console.warn('Maintenance batch query permission denied, skipping...', permissionError);
            }
          }
          
          const maintenanceMatches = allMaintenanceDocs.filter(doc => {
            const data = doc.data();
            const searchTerms = data.searchTerms || [];
            const matches = (
              data.title?.toLowerCase().includes(lowerSearchTerm) ||
              data.description?.toLowerCase().includes(lowerSearchTerm) ||
              data.status?.toLowerCase().includes(lowerSearchTerm) ||
              data.propertyAddress?.toLowerCase().includes(lowerSearchTerm) ||
              searchTerms.some(term => term.toLowerCase().includes(lowerSearchTerm))
            );
            return matches;
          }).slice(0, 5);

          if (maintenanceMatches.length > 0) {
            const maintenanceResults = {
              category: 'maintenance',
              icon: FaTools,
              items: maintenanceMatches.map(doc => {
                const data = doc.data();
                return {
                  id: doc.id,
                  title: data.title || 'Untitled Request',
                  subtitle: `${data.status || 'No status'} - ${data.propertyAddress || 'Unknown Property'}`,
                };
              }),
            };
            searchResults.push(maintenanceResults);
          }
      } else if (userRole === 'agent' && currentUser) {
        // For agents, get properties they manage first
        let agentPropertiesSnapshot;
        try {
          agentPropertiesSnapshot = await getDocs(
            query(collection(db, 'properties'), where('agentId', '==', currentUser.uid))
          );
        } catch (permissionError) {
          console.warn('Agent properties query permission denied, skipping...', permissionError);
          agentPropertiesSnapshot = { docs: [] };
        }
        
        const propertyIds = agentPropertiesSnapshot.docs.map(doc => doc.id);
        
        if (propertyIds.length > 0) {
          const batchSize = 10;
          let allMaintenanceDocs = [];
          
          for (let i = 0; i < propertyIds.length; i += batchSize) {
            const batchIds = propertyIds.slice(i, i + batchSize);
            try {
              const batchQuery = query(maintenanceRef, where('propertyId', 'in', batchIds));
              const batchSnapshot = await getDocs(batchQuery);
              allMaintenanceDocs = [...allMaintenanceDocs, ...batchSnapshot.docs];
            } catch (permissionError) {
              console.warn('Maintenance batch query permission denied, skipping...', permissionError);
            }
          }
          
          const maintenanceMatches = allMaintenanceDocs.filter(doc => {
            const data = doc.data();
            const searchTerms = data.searchTerms || [];
            const matches = (
              data.title?.toLowerCase().includes(lowerSearchTerm) ||
              data.description?.toLowerCase().includes(lowerSearchTerm) ||
              data.status?.toLowerCase().includes(lowerSearchTerm) ||
              data.propertyAddress?.toLowerCase().includes(lowerSearchTerm) ||
              searchTerms.some(term => term.toLowerCase().includes(lowerSearchTerm))
            );
            return matches;
          }).slice(0, 5);

          if (maintenanceMatches.length > 0) {
            const maintenanceResults = {
              category: 'maintenance',
              icon: FaTools,
              items: maintenanceMatches.map(doc => {
                const data = doc.data();
                return {
                  id: doc.id,
                  title: data.title || 'Untitled Request',
                  subtitle: `${data.status || 'No status'} - ${data.propertyAddress || 'Unknown Property'}`,
                };
              }),
            };
            searchResults.push(maintenanceResults);
          }
        }
      } else if (['owner', 'administrator'].includes(userRole)) {
        // For owners and administrators, search all maintenance requests
        let maintenanceSnapshot;
        try {
          maintenanceSnapshot = await getDocs(maintenanceRef);
        } catch (permissionError) {
          console.warn('Maintenance search permission denied, skipping...', permissionError);
          maintenanceSnapshot = { docs: [] };
        }
        
        const maintenanceMatches = maintenanceSnapshot.docs.filter(doc => {
          const data = doc.data();
          const searchTerms = data.searchTerms || [];
          const matches = (
            data.title?.toLowerCase().includes(lowerSearchTerm) ||
            data.description?.toLowerCase().includes(lowerSearchTerm) ||
            data.status?.toLowerCase().includes(lowerSearchTerm) ||
            data.propertyAddress?.toLowerCase().includes(lowerSearchTerm) ||
            searchTerms.some(term => term.toLowerCase().includes(lowerSearchTerm))
          );
          return matches;
        }).slice(0, 5);

        if (maintenanceMatches.length > 0) {
          const maintenanceResults = {
            category: 'maintenance',
            icon: FaTools,
            items: maintenanceMatches.map(doc => {
              const data = doc.data();
              return {
                id: doc.id,
                title: data.title || 'Untitled Request',
                subtitle: `${data.status || 'No status'} - ${data.propertyAddress || 'Unknown Property'}`,
              };
            }),
          };
          searchResults.push(maintenanceResults);
        }
      }

      // Search Landlords - use pre-fetched landlordsSnapshot from parallel wave
      if (['agent', 'owner', 'administrator'].includes(userRole)) {
        const landlordMatches = landlordsSnapshot.docs.filter(doc => {
          const data = doc.data();
          const searchTerms = data.searchTerms || [];
          const fullName = `${data.firstName || ''} ${data.lastName || ''}`.trim().toLowerCase();
          
          const matches = (
            data.firstName?.toLowerCase().includes(lowerSearchTerm) ||
            data.lastName?.toLowerCase().includes(lowerSearchTerm) ||
            data.email?.toLowerCase().includes(lowerSearchTerm) ||
            fullName.includes(lowerSearchTerm) ||
            data.phone?.toLowerCase().includes(lowerSearchTerm) ||
            data.company?.toLowerCase().includes(lowerSearchTerm) ||
            searchTerms.some(term => term.toLowerCase().includes(lowerSearchTerm))
          );
          return matches;
        }).slice(0, 5);

        if (landlordMatches.length > 0) {
          const landlordResults = {
            category: 'landlords',
            icon: FaUserTie,
            items: landlordMatches.map(doc => {
              const data = doc.data();
              return {
                id: doc.id,
                title: `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Unnamed Landlord',
                subtitle: data.email || data.phone || 'No contact info',
              };
            }),
          };
          searchResults.push(landlordResults);
        }
      }

      // Search Finance Records - Only when Finance module enabled, for owners and administrators
      if (isFeatureEnabled(FEATURES.FINANCE_MODULE) && ['owner', 'administrator'].includes(userRole)) {
        const financeRef = collection(db, 'finances');
        let financeQuery = financeRef;
        
        // For owners, only show their own finances
        if (userRole === 'owner' && currentUser) {
          financeQuery = query(financeRef, where('ownerId', '==', currentUser.uid));
        }
        
        let financeSnapshot;
        try {
          financeSnapshot = await getDocs(financeQuery);
        } catch (permissionError) {
          console.warn('Finance search permission denied, skipping...', permissionError);
          financeSnapshot = { docs: [] };
        }
        
        const financeMatches = financeSnapshot.docs.filter(doc => {
          const data = doc.data();
          const searchTerms = data.searchTerms || [];
          const matches = (
            data.type?.toLowerCase().includes(lowerSearchTerm) ||
            data.propertyAddress?.toLowerCase().includes(lowerSearchTerm) ||
            data.description?.toLowerCase().includes(lowerSearchTerm) ||
            searchTerms.some(term => term.toLowerCase().includes(lowerSearchTerm))
          );
          return matches;
        }).slice(0, 5);

        if (financeMatches.length > 0) {
          const financeResults = {
            category: 'finance',
            icon: FaPoundSign,
            items: financeMatches.map(doc => {
              const data = doc.data();
              return {
                id: doc.id,
                title: `${data.type} - £${data.amount}`,
                subtitle: `${data.propertyAddress} - ${new Date(data.date).toLocaleDateString()}`,
              };
            }),
          };
          searchResults.push(financeResults);
        }
      }

      // Search Links - use pre-fetched linksSnapshot from parallel wave
      const linkMatches = linksSnapshot.docs.filter(doc => {
        const data = doc.data();
        const searchTerms = data.searchTerms || [];
        const matches = (
          data.title?.toLowerCase().includes(lowerSearchTerm) ||
          data.description?.toLowerCase().includes(lowerSearchTerm) ||
          data.category?.toLowerCase().includes(lowerSearchTerm) ||
          data.url?.toLowerCase().includes(lowerSearchTerm) ||
          searchTerms.some(term => term.toLowerCase().includes(lowerSearchTerm))
        );
        return matches;
      }).slice(0, 5);

      if (linkMatches.length > 0) {
        const linkResults = {
          category: 'links',
          icon: FaChevronRight,
          items: linkMatches.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              title: data.title || 'Untitled Link',
              subtitle: data.category || data.description || 'No description',
            };
          }),
        };
        searchResults.push(linkResults);
      }

      // Search Documents - Role-based filtering
      const documentsRef = collection(db, 'documents');
      let documentsQuery = documentsRef;
      
      if (userRole === 'landlord' && currentUser) {
        // For landlords, get their property IDs first
        let landlordPropertiesSnapshot;
        try {
          landlordPropertiesSnapshot = await getDocs(
            query(collection(db, 'properties'), where('landlordId', '==', currentUser.uid))
          );
        } catch (permissionError) {
          console.warn('Landlord properties query permission denied, skipping...', permissionError);
          landlordPropertiesSnapshot = { docs: [] };
        }
        
        const propertyIds = landlordPropertiesSnapshot.docs.map(doc => doc.id);
        
        if (propertyIds.length > 0) {
          // Search documents for landlord's properties only
          let documentsSnapshot;
          try {
            documentsSnapshot = await getDocs(documentsRef);
          } catch (permissionError) {
            console.warn('Documents search permission denied, skipping...', permissionError);
            documentsSnapshot = { docs: [] };
          }
          
          const documentMatches = documentsSnapshot.docs.filter(doc => {
            const data = doc.data();
            const searchTerms = data.searchTerms || [];
            const isOwnedProperty = propertyIds.includes(data.propertyId);
            const matches = isOwnedProperty && (
              data.name?.toLowerCase().includes(lowerSearchTerm) ||
              data.type?.toLowerCase().includes(lowerSearchTerm) ||
              data.category?.toLowerCase().includes(lowerSearchTerm) ||
              data.description?.toLowerCase().includes(lowerSearchTerm) ||
              data.fileName?.toLowerCase().includes(lowerSearchTerm) ||
              searchTerms.some(term => term.toLowerCase().includes(lowerSearchTerm))
            );
            return matches;
          }).slice(0, 5);

          if (documentMatches.length > 0) {
            const documentResults = {
              category: 'documents',
              icon: FaFileDownload,
              items: documentMatches.map(doc => {
                const data = doc.data();
                const address = data.propertyAddress || (data.propertyId ? propertyAddressById.get(data.propertyId) : null);
                return {
                  id: doc.id,
                  propertyId: data.propertyId,
                  title: data.name || data.fileName || 'Untitled Document',
                  subtitle: `${data.type || 'Document'} - ${address || 'Unknown Property'}`,
                };
              }),
            };
            searchResults.push(documentResults);
          }
        }
      } else {
        // For other roles, search all documents
        let documentsSnapshot;
        try {
          documentsSnapshot = await getDocs(documentsRef);
        } catch (permissionError) {
          console.warn('Documents search permission denied, skipping...', permissionError);
          documentsSnapshot = { docs: [] };
        }
        
        const documentMatches = documentsSnapshot.docs.filter(doc => {
          const data = doc.data();
          const searchTerms = data.searchTerms || [];
          const matches = (
            data.name?.toLowerCase().includes(lowerSearchTerm) ||
            data.type?.toLowerCase().includes(lowerSearchTerm) ||
            data.category?.toLowerCase().includes(lowerSearchTerm) ||
            data.description?.toLowerCase().includes(lowerSearchTerm) ||
            data.fileName?.toLowerCase().includes(lowerSearchTerm) ||
            searchTerms.some(term => term.toLowerCase().includes(lowerSearchTerm))
          );
          return matches;
        }).slice(0, 5);

        if (documentMatches.length > 0) {
          const documentResults = {
            category: 'documents',
            icon: FaFileDownload,
            items: documentMatches.map(doc => {
              const data = doc.data();
              const address = data.propertyAddress || (data.propertyId ? propertyAddressById.get(data.propertyId) : null);
              return {
                id: doc.id,
                propertyId: data.propertyId,
                title: data.name || data.fileName || 'Untitled Document',
                subtitle: `${data.type || 'Document'} - ${address || 'Unknown Property'}`,
              };
            }),
          };
          searchResults.push(documentResults);
        }
      }

      // Search Users - Only for owners and administrators
      if (['owner', 'administrator'].includes(userRole)) {
        const usersRef = collection(db, 'users');
        let usersSnapshot;
        try {
          usersSnapshot = await getDocs(usersRef);
        } catch (permissionError) {
          console.warn('Users search permission denied, skipping...', permissionError);
          usersSnapshot = { docs: [] };
        }
        
        const userMatches = usersSnapshot.docs.filter(doc => {
          const data = doc.data();
          const searchTerms = data.searchTerms || [];
          const fullName = `${data.firstName || ''} ${data.lastName || ''}`.trim().toLowerCase();
          
          const matches = (
            data.firstName?.toLowerCase().includes(lowerSearchTerm) ||
            data.lastName?.toLowerCase().includes(lowerSearchTerm) ||
            data.email?.toLowerCase().includes(lowerSearchTerm) ||
            fullName.includes(lowerSearchTerm) ||
            data.phone?.toLowerCase().includes(lowerSearchTerm) ||
            data.role?.toLowerCase().includes(lowerSearchTerm) ||
            data.company?.toLowerCase().includes(lowerSearchTerm) ||
            searchTerms.some(term => term.toLowerCase().includes(lowerSearchTerm))
          );
          return matches;
        }).slice(0, 5);

        if (userMatches.length > 0) {
          const userResults = {
            category: 'users',
            icon: FaUserTie,
            items: userMatches.map(doc => {
              const data = doc.data();
              return {
                id: doc.id,
                title: `${data.firstName || ''} ${data.lastName || ''}`.trim() || data.email || 'Unnamed User',
                subtitle: `${data.role || 'No role'} - ${data.email || 'No email'}`,
              };
            }),
          };
          searchResults.push(userResults);
        }
      }

      // Search Payments - Only when Finance module enabled, for owners and administrators
      if (isFeatureEnabled(FEATURES.FINANCE_MODULE) && ['owner', 'administrator'].includes(userRole)) {
        const paymentsRef = collection(db, 'payments');
        let paymentsQuery = paymentsRef;
        
        // For owners, filter by their properties
        if (userRole === 'owner' && currentUser) {
          // Get owner's properties first
          let ownerPropertiesSnapshot;
          try {
            ownerPropertiesSnapshot = await getDocs(
              query(collection(db, 'properties'), where('landlordId', '==', currentUser.uid))
            );
          } catch (permissionError) {
            console.warn('Owner properties query permission denied, skipping...', permissionError);
            ownerPropertiesSnapshot = { docs: [] };
          }
          
          const propertyIds = ownerPropertiesSnapshot.docs.map(doc => doc.id);
          
          if (propertyIds.length > 0) {
            // Fetch payments and filter by property IDs
            let paymentsSnapshot;
            try {
              paymentsSnapshot = await getDocs(paymentsRef);
            } catch (permissionError) {
              console.warn('Payments search permission denied, skipping...', permissionError);
              paymentsSnapshot = { docs: [] };
            }
            
            const paymentMatches = paymentsSnapshot.docs.filter(doc => {
              const data = doc.data();
              const searchTerms = data.searchTerms || [];
              const isOwnedProperty = propertyIds.includes(data.propertyId);
              const matches = isOwnedProperty && (
                data.tenantName?.toLowerCase().includes(lowerSearchTerm) ||
                data.propertyAddress?.toLowerCase().includes(lowerSearchTerm) ||
                data.type?.toLowerCase().includes(lowerSearchTerm) ||
                data.status?.toLowerCase().includes(lowerSearchTerm) ||
                data.amount?.toString().includes(lowerSearchTerm) ||
                searchTerms.some(term => term.toLowerCase().includes(lowerSearchTerm))
              );
              return matches;
            }).slice(0, 5);

            if (paymentMatches.length > 0) {
              const paymentResults = {
                category: 'payments',
                icon: FaCreditCard,
                items: paymentMatches.map(doc => {
                  const data = doc.data();
                  const dueDate = data.dueDate?.toDate ? data.dueDate.toDate() : (data.dueDate ? new Date(data.dueDate) : null);
                  return {
                    id: doc.id,
                    title: `£${data.amount || '0'} - ${data.type || 'Payment'}`,
                    subtitle: `${data.propertyAddress || 'Unknown Property'} - ${dueDate ? dueDate.toLocaleDateString() : 'No date'}`,
                  };
                }),
              };
              searchResults.push(paymentResults);
            }
          }
        } else {
          // Administrators can see all payments
          let paymentsSnapshot;
          try {
            paymentsSnapshot = await getDocs(paymentsQuery);
          } catch (permissionError) {
            console.warn('Payments search permission denied, skipping...', permissionError);
            paymentsSnapshot = { docs: [] };
          }
          
          const paymentMatches = paymentsSnapshot.docs.filter(doc => {
            const data = doc.data();
            const searchTerms = data.searchTerms || [];
            const matches = (
              data.tenantName?.toLowerCase().includes(lowerSearchTerm) ||
              data.propertyAddress?.toLowerCase().includes(lowerSearchTerm) ||
              data.type?.toLowerCase().includes(lowerSearchTerm) ||
              data.status?.toLowerCase().includes(lowerSearchTerm) ||
              data.amount?.toString().includes(lowerSearchTerm) ||
              searchTerms.some(term => term.toLowerCase().includes(lowerSearchTerm))
            );
            return matches;
          }).slice(0, 5);

          if (paymentMatches.length > 0) {
            const paymentResults = {
              category: 'payments',
              icon: FaCreditCard,
              items: paymentMatches.map(doc => {
                const data = doc.data();
                const dueDate = data.dueDate?.toDate ? data.dueDate.toDate() : (data.dueDate ? new Date(data.dueDate) : null);
                return {
                  id: doc.id,
                  title: `£${data.amount || '0'} - ${data.type || 'Payment'}`,
                  subtitle: `${data.propertyAddress || 'Unknown Property'} - ${dueDate ? dueDate.toLocaleDateString() : 'No date'}`,
                };
              }),
            };
            searchResults.push(paymentResults);
          }
        }
      }

      // Search Certificates - Role-based filtering
      if (['agent', 'owner', 'administrator', 'landlord'].includes(userRole)) {
        const certificatesRef = collection(db, 'certificates');
        let certificatesQuery = certificatesRef;
        
        // For landlords, filter by their properties
        if (userRole === 'landlord' && currentUser) {
          let landlordPropertiesSnapshot;
          try {
            landlordPropertiesSnapshot = await getDocs(
              query(collection(db, 'properties'), where('landlordId', '==', currentUser.uid))
            );
          } catch (permissionError) {
            console.warn('Landlord properties query permission denied, skipping...', permissionError);
            landlordPropertiesSnapshot = { docs: [] };
          }
          
          const propertyIds = landlordPropertiesSnapshot.docs.map(doc => doc.id);
          
          if (propertyIds.length > 0) {
            let certificatesSnapshot;
            try {
              certificatesSnapshot = await getDocs(certificatesRef);
            } catch (permissionError) {
              console.warn('Certificates search permission denied, skipping...', permissionError);
              certificatesSnapshot = { docs: [] };
            }
            
            const certificateMatches = certificatesSnapshot.docs.filter(doc => {
              const data = doc.data();
              const searchTerms = data.searchTerms || [];
              const isOwnedProperty = propertyIds.includes(data.propertyId);
              const matches = isOwnedProperty && (
                data.type?.toLowerCase().includes(lowerSearchTerm) ||
                data.propertyAddress?.toLowerCase().includes(lowerSearchTerm) ||
                data.status?.toLowerCase().includes(lowerSearchTerm) ||
                data.certificateNumber?.toLowerCase().includes(lowerSearchTerm) ||
                searchTerms.some(term => term.toLowerCase().includes(lowerSearchTerm))
              );
              return matches;
            }).slice(0, 5);

            if (certificateMatches.length > 0) {
              const certificateResults = {
                category: 'certificates',
                icon: FaCertificate,
                items: certificateMatches.map(doc => {
                  const data = doc.data();
                  const expiryDate = data.expiryDate?.toDate ? data.expiryDate.toDate() : (data.expiryDate ? new Date(data.expiryDate) : null);
                  return {
                    id: doc.id,
                    title: `${data.type || 'Certificate'} - ${data.propertyAddress || 'Unknown Property'}`,
                    subtitle: `Expires: ${expiryDate ? expiryDate.toLocaleDateString() : 'No expiry date'}`,
                  };
                }),
              };
              searchResults.push(certificateResults);
            }
          }
        } else {
          // For agents/owners/admins, search all certificates
          let certificatesSnapshot;
          try {
            certificatesSnapshot = await getDocs(certificatesQuery);
          } catch (permissionError) {
            console.warn('Certificates search permission denied, skipping...', permissionError);
            certificatesSnapshot = { docs: [] };
          }
          
          const certificateMatches = certificatesSnapshot.docs.filter(doc => {
            const data = doc.data();
            const searchTerms = data.searchTerms || [];
            const matches = (
              data.type?.toLowerCase().includes(lowerSearchTerm) ||
              data.propertyAddress?.toLowerCase().includes(lowerSearchTerm) ||
              data.status?.toLowerCase().includes(lowerSearchTerm) ||
              data.certificateNumber?.toLowerCase().includes(lowerSearchTerm) ||
              searchTerms.some(term => term.toLowerCase().includes(lowerSearchTerm))
            );
            return matches;
          }).slice(0, 5);

          if (certificateMatches.length > 0) {
            const certificateResults = {
              category: 'certificates',
              icon: FaCertificate,
              items: certificateMatches.map(doc => {
                const data = doc.data();
                const expiryDate = data.expiryDate?.toDate ? data.expiryDate.toDate() : (data.expiryDate ? new Date(data.expiryDate) : null);
                return {
                  id: doc.id,
                  title: `${data.type || 'Certificate'} - ${data.propertyAddress || 'Unknown Property'}`,
                  subtitle: `Expires: ${expiryDate ? expiryDate.toLocaleDateString() : 'No expiry date'}`,
                };
              }),
            };
            searchResults.push(certificateResults);
          }
        }
      }

      // Search Calendar Events - Role-based filtering
      if (['agent', 'owner', 'administrator', 'landlord'].includes(userRole)) {
        const eventsRef = collection(db, 'events');
        let eventsQuery = eventsRef;
        
        // For landlords, filter by their properties
        if (userRole === 'landlord' && currentUser) {
          let landlordPropertiesSnapshot;
          try {
            landlordPropertiesSnapshot = await getDocs(
              query(collection(db, 'properties'), where('landlordId', '==', currentUser.uid))
            );
          } catch (permissionError) {
            console.warn('Landlord properties query permission denied, skipping...', permissionError);
            landlordPropertiesSnapshot = { docs: [] };
          }
          
          const propertyIds = landlordPropertiesSnapshot.docs.map(doc => doc.id);
          
          if (propertyIds.length > 0) {
            let eventsSnapshot;
            try {
              eventsSnapshot = await getDocs(eventsRef);
            } catch (permissionError) {
              console.warn('Calendar events search permission denied, skipping...', permissionError);
              eventsSnapshot = { docs: [] };
            }
            
            const eventMatches = eventsSnapshot.docs.filter(doc => {
              const data = doc.data();
              const searchTerms = data.searchTerms || [];
              const isOwnedProperty = !data.propertyId || propertyIds.includes(data.propertyId);
              const matches = isOwnedProperty && (
                data.title?.toLowerCase().includes(lowerSearchTerm) ||
                data.description?.toLowerCase().includes(lowerSearchTerm) ||
                data.type?.toLowerCase().includes(lowerSearchTerm) ||
                data.location?.toLowerCase().includes(lowerSearchTerm) ||
                searchTerms.some(term => term.toLowerCase().includes(lowerSearchTerm))
              );
              return matches;
            }).slice(0, 5);

            if (eventMatches.length > 0) {
              const calendarResults = {
                category: 'calendar',
                icon: FaCalendar,
                items: eventMatches.map(doc => {
                  const data = doc.data();
                  const eventDate = data.startDate?.toDate ? data.startDate.toDate() : (data.startDate ? new Date(data.startDate) : null);
                  return {
                    id: doc.id,
                    title: data.title || 'Untitled Event',
                    subtitle: `${eventDate ? eventDate.toLocaleDateString() : 'No date'} - ${data.type || 'Event'}`,
                  };
                }),
              };
              searchResults.push(calendarResults);
            }
          }
        } else {
          // For agents/owners/admins, search all events
          let eventsSnapshot;
          try {
            eventsSnapshot = await getDocs(eventsQuery);
          } catch (permissionError) {
            console.warn('Calendar events search permission denied, skipping...', permissionError);
            eventsSnapshot = { docs: [] };
          }
          
          const eventMatches = eventsSnapshot.docs.filter(doc => {
            const data = doc.data();
            const searchTerms = data.searchTerms || [];
            const matches = (
              data.title?.toLowerCase().includes(lowerSearchTerm) ||
              data.description?.toLowerCase().includes(lowerSearchTerm) ||
              data.type?.toLowerCase().includes(lowerSearchTerm) ||
              data.location?.toLowerCase().includes(lowerSearchTerm) ||
              searchTerms.some(term => term.toLowerCase().includes(lowerSearchTerm))
            );
            return matches;
          }).slice(0, 5);

          if (eventMatches.length > 0) {
            const calendarResults = {
              category: 'calendar',
              icon: FaCalendar,
              items: eventMatches.map(doc => {
                const data = doc.data();
                const eventDate = data.startDate?.toDate ? data.startDate.toDate() : (data.startDate ? new Date(data.startDate) : null);
                return {
                  id: doc.id,
                  title: data.title || 'Untitled Event',
                  subtitle: `${eventDate ? eventDate.toLocaleDateString() : 'No date'} - ${data.type || 'Event'}`,
                };
              }),
            };
            searchResults.push(calendarResults);
          }
        }
      }

      // Messages/conversations are excluded from search (not shown)
      
      // Apply category filters
      if (isStale()) return;
      const filteredResults = filterResultsByCategory(searchResults);
      setSearchResults(filteredResults);
    } catch (error) {
      if (isStale()) return;
      console.error('Search error:', error);
      setSearchResults([{
        category: 'error',
        items: [{
          id: 'error',
          title: 'Error performing search',
          subtitle: 'Please try again later'
        }]
      }]);
    } finally {
      if (!isStale()) setIsSearching(false);
    }
  };

  // Memoize performSearch to prevent recreating on every render
  const memoizedPerformSearch = useCallback(performSearch, [userRole, currentUser]);

  // Debounce search to prevent too many queries (250ms for snappier feel)
  useEffect(() => {
    const timer = setTimeout(() => {
      memoizedPerformSearch(searchQuery);
    }, 250);

    return () => clearTimeout(timer);
  }, [searchQuery, memoizedPerformSearch]);

  // Re-run search when category filters change
  useEffect(() => {
    if (searchQuery && searchQuery.length >= 2) {
      memoizedPerformSearch(searchQuery);
    }
  }, [selectedCategories, searchQuery, memoizedPerformSearch]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Command/Ctrl + K to open search (focus handled by useEffect when modal opens)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearchModal(true);
      }
      // Escape to close search modal
      if (e.key === 'Escape') {
        setShowSearchModal(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Auto-focus search input when modal opens so user can type immediately
  useEffect(() => {
    if (!showSearchModal) return;
    const timeoutId = setTimeout(() => {
      searchInputRef.current?.focus();
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [showSearchModal]);

  // Handle search input change
  const handleSearchInputChange = useCallback((event) => {
    setSearchQuery(event.target.value);
  }, []);

  return (
    <div className={`
      topbar 
      ${isDarkMode ? 'text-white' : 'text-gray-900'}
      transition-all duration-300 ease-in-out
      w-full
    `}
    style={{
      position: 'sticky',
      top: 0,
      zIndex: 50,
      backgroundColor: isDarkMode ? 'rgba(17, 24, 39, 0.4)' : 'rgba(255, 255, 255, 0.4)',
      backdropFilter: 'blur(12px) saturate(180%)',
      WebkitBackdropFilter: 'blur(12px) saturate(180%)',
      borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.05)',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
    }}>
      {/* Header structure from the new design */}
      <div className="header">
        <div className="logo-section">
          <div className="logo" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }}>
            {logoUrl ? (
              <img 
                src={logoUrl} 
                alt="Company Logo" 
                className="w-full h-full object-contain"
              />
            ) : (
              <img 
                src="/favicon.jpg" 
                alt="Plutoo Default Logo" 
                className="w-full h-full object-contain"
              />
            )}
          </div>
          <div className="user-greeting">
            <h1 className={isDarkMode ? 'text-white' : 'text-gray-900'}>
              {welcomeMessage}, {currentUser?.displayName || currentUser?.email?.split('@')[0] || 'User'}
            </h1>
            <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {currentDate}
            </p>
          </div>
        </div>
        
        <div className="search-section">
          {(isMobile || isTablet) ? (
            <div
              className="icon-circle"
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowSearchModal(true);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setShowSearchModal(true);
                }
              }}
              ref={searchButtonRef}
              style={{ color: primaryColor }}
              aria-label="Open search"
            >
              <FaSearch size={16} />
            </div>
          ) : (
            <div 
              className="search-bar search-bar--pill"
              style={searchPillStyle}
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowSearchModal(true);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setShowSearchModal(true);
                }
              }}
              ref={searchButtonRef}
            >
              <div className="search-icon search-icon--pill" aria-hidden>
                <FaSearch size={11} />
              </div>
              <input 
                type="text" 
                className="search-input search-input--pill"
                placeholder="Search"
                readOnly
                tabIndex={-1}
              />
            </div>
          )}
          
          {!isMobile && (
            <>
              {checkTopBarLinkAccess('topBarProperties') && (
                <NewCustomButton onClick={() => navigate('/properties')} compact>
                  Properties
                </NewCustomButton>
              )}
              {checkTopBarLinkAccess('topBarMaintenance') && (
                <NewCustomButton onClick={() => navigate('/maintenance')} compact>
                  Maintenance
                </NewCustomButton>
              )}
              {checkTopBarLinkAccess('topBarCompliance') && (
                <NewCustomButton onClick={() => navigate('/compliance')} compact>
                  Compliance
                </NewCustomButton>
              )}
              {checkTopBarLinkAccess('topBarContacts') && (
                <NewCustomButton onClick={() => navigate('/contacts')} compact>
                  Contacts
                </NewCustomButton>
              )}
            </>
          )}
          
          {currentUser && checkPageAccess('notifications') && (
            <button
              type="button"
              className="icon-circle"
              onClick={handleNotificationsClick}
              style={{ color: primaryColor, position: 'relative' }}
              aria-label={
                notificationCount > 0
                  ? `Notifications, ${notificationCount} unread`
                  : 'Notifications'
              }
            >
              <FaBell size={16} aria-hidden />
              {notificationCount > 0 && (
                <span className="notification-count">
                  {notificationCount > 99 ? '99+' : notificationCount}
                </span>
              )}
            </button>
          )}
          
          {userRole && hasFeatureAccessSync(userRole, 'accessSettings') && (
            <div 
              className="icon-circle"
              onClick={toggleTheme}
              style={{ color: primaryColor }}
            >
              {isDarkMode ? (
                <FaMoon size={16} />
              ) : (
                <FaSun size={16} />
              )}
            </div>
          )}
          
          <Menu as="div" className="relative">
            <Menu.Button className="icon-circle overflow-hidden">
              {currentUser?.photoURL ? (
                <img
                  src={currentUser.photoURL}
                  alt="Profile"
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <div 
                  className="w-full h-full rounded-full flex items-center justify-center text-white text-xs font-medium" 
                  style={{ backgroundColor: primaryColor }}
                >
                  {getUserInitial()}
                </div>
              )}
            </Menu.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 z-50 mt-2 w-64 rounded-xl bg-white dark:bg-gray-900 shadow-lg ring-1 ring-black ring-opacity-5 divide-y divide-gray-100 dark:divide-gray-800 focus:outline-none">
                <div className="px-4 py-4">
                  <div className="flex items-center">
                    <div className="relative w-12 h-12">
                      {currentUser?.photoURL ? (
                        <img
                          src={currentUser.photoURL}
                          alt="Profile"
                          className="rounded-full object-cover w-full h-full ring-2 ring-white dark:ring-gray-800"
                        />
                      ) : (
                        <div 
                          className="w-full h-full rounded-full flex items-center justify-center text-white text-xl font-medium" 
                          style={{ backgroundColor: primaryColor }}
                        >
                          {getUserInitial()}
                        </div>
                      )}
                      <div 
                        className="absolute bottom-0 right-0 w-3 h-3 rounded-full ring-2 ring-white dark:ring-gray-800"
                        style={{ backgroundColor: secondaryColor }}
                      >
                      </div>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {currentUser?.displayName || currentUser?.email?.split('@')[0]}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {currentUser?.email}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="py-1">
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={() => navigate('/profile')}
                        className={`${
                          active ? 'bg-gray-50 dark:bg-gray-800' : ''
                        } group flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100`}
                      >
                        <FaUserCircle className="w-5 h-5 mr-3 text-gray-400 group-hover" style={{ color: active ? primaryColor : 'inherit' }} />
                        Profile
                      </button>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={() => navigate('/account-management')}
                        className={`${
                          active ? 'bg-gray-50 dark:bg-gray-800' : ''
                        } group flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100`}
                      >
                        <FaShieldAlt className="w-5 h-5 mr-3 text-gray-400 group-hover" style={{ color: active ? primaryColor : 'inherit' }} />
                        Account Security
                      </button>
                    )}
                  </Menu.Item>
                  {userRole !== 'landlord' && (
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={() => navigate('/settings')}
                          className={`${
                            active ? 'bg-gray-50 dark:bg-gray-800' : ''
                          } group flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100`}
                        >
                          <FaCog className="w-5 h-5 mr-3 text-gray-400 group-hover" style={{ color: active ? primaryColor : 'inherit' }} />
                          Settings
                        </button>
                      )}
                    </Menu.Item>
                  )}
                </div>

                <div className="py-1">
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={handleLogout}
                        disabled={logoutLoading}
                        className={`${
                          active ? 'bg-red-50 dark:bg-red-900/20' : ''
                        } group flex w-full items-center px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300`}
                      >
                        {logoutLoading ? (
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                            className="w-5 h-5 border-2 border-red-500 border-t-transparent rounded-full"
                          />
                        ) : (
                          <FaSignOutAlt className="w-5 h-5 mr-3 text-red-400 group-hover:text-red-500" />
                        )}
                        {logoutLoading ? 'Logging out...' : 'Sign out'}
                      </button>
                    )}
                  </Menu.Item>
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
          
          {(isMobile || isTablet) && (
            <button
              onClick={toggleSidebar}
              className="icon-circle"
            >
              <FaBars size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Search Modal - Positioned outside TopBar container */}
      <AnimatePresence>
        {showSearchModal && (
          <motion.div 
            className="fixed inset-0" 
            style={{ 
              zIndex: 99999
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            {/* Clean Backdrop with Smooth Fade */}
            <motion.div 
              className="fixed inset-0"
              style={{
                background: 'transparent'
              }}
              onClick={() => setShowSearchModal(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              {/* Blur layer with gradient mask */}
              <div 
                style={{
                  position: 'absolute',
                  inset: 0,
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  maskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 60%, rgba(0,0,0,0.8) 75%, rgba(0,0,0,0.4) 85%, rgba(0,0,0,0) 100%)',
                  WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 60%, rgba(0,0,0,0.8) 75%, rgba(0,0,0,0.4) 85%, rgba(0,0,0,0) 100%)',
                  pointerEvents: 'none'
                }}
              />
            </motion.div>
            
            {/* Modal Content */}
            <div className="fixed inset-0 flex items-start justify-center px-3 sm:px-4 pt-20 sm:pt-20">
              {/* Mobile-optimized glow effect */}
              <div 
                className="absolute top-16 sm:top-16 left-1/2 transform -translate-x-1/2 w-full max-w-lg sm:max-w-4xl h-48 sm:h-96 opacity-30 sm:opacity-20 pointer-events-none"
                style={{
                  background: isDarkMode
                    ? 'radial-gradient(ellipse at center, rgba(59, 130, 246, 0.4) 0%, transparent 70%)'
                    : 'radial-gradient(ellipse at center, rgba(59, 130, 246, 0.3) 0%, transparent 70%)',
                  filter: 'blur(30px)'
                }}
              />
              <motion.div 
                ref={modalRef}
                className="w-full max-w-sm sm:max-w-3xl rounded-2xl overflow-hidden relative"
                style={{
                  maxHeight: 'calc(100vh - 8rem)',
                  background: isDarkMode 
                    ? 'linear-gradient(145deg, rgba(31, 41, 55, 0.98) 0%, rgba(17, 24, 39, 0.99) 100%)'
                    : 'linear-gradient(145deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.99) 100%)',
                  backdropFilter: 'blur(24px) saturate(180%)',
                  WebkitBackdropFilter: 'blur(24px) saturate(180%)',
                  boxShadow: isDarkMode
                    ? '0 25px 50px -12px rgba(0, 0, 0, 0.9), 0 0 0 1px rgba(255, 255, 255, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.15)'
                    : '0 25px 50px -12px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                  border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.08)'
                }}
                initial={{ opacity: 0, scale: 0.96, y: -12 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.98, y: -8 }}
                transition={{ type: 'spring', damping: 28, stiffness: 380 }}
              >
              <Combobox
                value={searchQuery}
                onChange={(item) => {
                  if (!item) return;
                  setShowSearchModal(false);
                  if (item.category === 'properties') {
                    navigate(`/properties/${item.id}`);
                  } else if (item.category === 'tenants') {
                    navigate(`/tenants/${item.id}`);
                  } else if (item.category === 'maintenance') {
                    navigate(`/maintenance/${item.id}`);
                  } else if (item.category === 'finance') {
                    navigate(`/finance/${item.id}`);
                  } else if (item.category === 'payments') {
                    navigate(`/finance`);
                  } else if (item.category === 'landlords') {
                    navigate(`/landlords/${item.id}`);
                  } else if (item.category === 'users') {
                    navigate(`/settings?tab=users&userId=${item.id}`);
                  } else if (item.category === 'links') {
                    navigate('/links');
                  } else if (item.category === 'documents') {
                    // Navigate to property's documents tab when propertyId exists
                    navigate(item.propertyId ? `/properties/${item.propertyId}?tab=documents` : '/properties');
                  } else if (item.category === 'certificates') {
                    navigate(`/compliance`);
                  } else if (item.category === 'calendar') {
                    navigate(`/calendar`);
                  } else if (item.category === 'messages') {
                    navigate(`/messages?conversation=${item.id}`);
                  } else if (item.category === 'pages' && item.path) {
                    navigate(item.path);
                  }
                }}
              >
                <motion.div 
                  className="relative"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.12 }}
                >
                  <div>
                    <FaSearch
                      className="pointer-events-none absolute left-4 top-4 h-5 w-5 text-gray-400"
                      style={{
                        filter: isDarkMode 
                          ? 'drop-shadow(0 1px 3px rgba(0, 0, 0, 0.4))' 
                          : 'drop-shadow(0 1px 3px rgba(0, 0, 0, 0.15))'
                      }}
                      aria-hidden="true"
                    />
                    <button
                      type="button"
                      className={`absolute right-4 top-4 h-5 w-5 transition-colors duration-200 ${
                        showFilters
                          ? isDarkMode ? 'text-blue-400' : 'text-blue-600'
                          : isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
                      }`}
                      onClick={() => setShowFilters(!showFilters)}
                      title="Toggle search filters"
                    >
                      <FaFilter />
                    </button>
                  </div>
                  <div>
                    <Combobox.Input
                      className={`h-14 w-full border-0 pl-12 pr-12 focus:ring-0 text-base font-medium transition-all duration-300 rounded-xl ${
                        isDarkMode 
                          ? 'bg-gray-800/90 text-white placeholder:text-gray-400' 
                          : 'bg-white/90 text-gray-900 placeholder:text-gray-500'
                      }`}
                      style={{
                        backdropFilter: 'blur(12px)',
                        WebkitBackdropFilter: 'blur(12px)',
                        boxShadow: isDarkMode
                          ? 'inset 0 2px 6px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)'
                          : 'inset 0 2px 6px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.05)',
                        border: 'none',
                        fontSize: '16px' // Prevents zoom on iOS
                      }}
                      placeholder="Search properties, tenants, maintenance…"
                      onChange={handleSearchInputChange}
                      ref={searchInputRef}
                    />
                  </div>
                </motion.div>

                {/* Advanced Search Filters */}
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ 
                    opacity: showFilters ? 1 : 0, 
                    height: showFilters ? 'auto' : 0 
                  }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className={`px-4 py-3 border-t ${
                    isDarkMode 
                      ? 'border-gray-700 bg-gray-800/50' 
                      : 'border-gray-200 bg-gray-50/50'
                  }`}>
                    <div className="flex flex-wrap gap-2">
                      <span className={`text-xs font-medium ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        Filter by:
                      </span>
                      {getAvailableCategories().map((category) => {
                        const isSelected = selectedCategories.includes(category.id);
                        const IconComponent = category.icon;
                        return (
                          <motion.button
                            key={category.id}
                            onClick={() => toggleCategory(category.id)}
                            className={`flex items-center px-2 py-1 rounded-md text-xs font-medium transition-all duration-200 ${
                              isSelected
                                ? isDarkMode
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-blue-500 text-white'
                                : isDarkMode
                                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                            whileTap={{ scale: 0.95 }}
                          >
                            <IconComponent className="w-3 h-3 mr-1" />
                            {category.name}
                          </motion.button>
                        );
                      })}
                      {selectedCategories.length > 0 && (
                        <motion.button
                          onClick={() => setSelectedCategories([])}
                          className={`text-xs font-medium px-2 py-1 rounded-md transition-colors duration-200 ${
                            isDarkMode
                              ? 'text-red-400 hover:bg-red-900/20'
                              : 'text-red-600 hover:bg-red-50'
                          }`}
                          whileTap={{ scale: 0.95 }}
                        >
                          Clear filters
                        </motion.button>
                      )}
                    </div>
                  </div>
                </motion.div>

                {searchResults.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.15 }}
                  >
                    <Combobox.Options 
                      static 
                      className={`overflow-y-auto text-sm ${
                        isDarkMode ? 'text-gray-200' : 'text-gray-800'
                      }`}
                      style={{
                        maxHeight: 'calc(100vh - 12rem)',
                        overflowY: 'auto',
                        backgroundColor: isDarkMode ? 'rgba(17, 24, 39, 0.5)' : 'rgba(248, 250, 252, 0.8)',
                        borderTop: isDarkMode 
                          ? '1px solid rgba(255, 255, 255, 0.08)' 
                          : '1px solid rgba(0, 0, 0, 0.06)',
                        WebkitOverflowScrolling: 'touch',
                        borderRadius: '0 0 1rem 1rem',
                        padding: '0.5rem 0.75rem 1rem'
                      }}
                    >
                    {searchResults.map((category, categoryIndex) => {
                      const IconComponent = category.icon;
                      return (
                        <div key={category.category} className="mb-4 last:mb-0">
                          <div className={`flex items-center gap-2 px-2 py-2 mb-1 rounded-md ${
                            isDarkMode ? 'bg-white/5' : 'bg-black/5'
                          }`}>
                            {IconComponent && (
                              <span 
                                className="flex items-center justify-center w-6 h-6 rounded-md shrink-0"
                                style={{ backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)' }}
                              >
                                <IconComponent className="w-3.5 h-3.5" style={{ color: primaryColor }} />
                              </span>
                            )}
                            <span className={`text-xs font-semibold uppercase tracking-wider ${
                              isDarkMode ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                              {category.category}
                            </span>
                          </div>
                          <div className="space-y-1">
                            {category.items.map((item) => (
                              <Combobox.Option
                                key={item.id}
                                value={{ ...item, category: category.category }}
                                className="cursor-pointer select-none rounded-lg transition-all duration-150 touch-manipulation outline-none"
                              >
                                {({ active }) => (
                                  <div
                                    className={`flex items-start gap-3 px-3 py-2.5 rounded-lg transition-all duration-150 ${
                                      active 
                                        ? ''
                                        : (isDarkMode ? 'hover:bg-white/5' : 'hover:bg-black/[0.03]')
                                    }`}
                                    style={active ? { 
                                      backgroundColor: `${primaryColor}18`,
                                      boxShadow: `inset 0 0 0 1px ${primaryColor}50`
                                    } : {}}
                                  >
                                    <span 
                                      className="flex items-center justify-center w-9 h-9 rounded-lg shrink-0 mt-0.5"
                                      style={{ 
                                        backgroundColor: active ? `${primaryColor}25` : (isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)')
                                      }}
                                    >
                                      {IconComponent && (
                                        <IconComponent className="w-4 h-4" style={{ color: active ? primaryColor : (isDarkMode ? '#9ca3af' : '#6b7280') }} />
                                      )}
                                    </span>
                                    <div className="flex-1 min-w-0 py-0.5">
                                      <p 
                                        className={`font-medium truncate ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}
                                        style={active ? { color: primaryColor } : {}}
                                      >
                                        {item.title}
                                      </p>
                                      {item.subtitle && (
                                        <p className="text-xs text-gray-500 truncate mt-0.5">
                                          {item.subtitle}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </Combobox.Option>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                    </Combobox.Options>
                  </motion.div>
                )}

                {isSearching && searchResults.length === 0 && (
                  <div className={`px-4 py-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className="flex items-center gap-3 px-2 py-3">
                      <div className="h-9 w-9 shrink-0 rounded-lg animate-pulse" style={{ backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)' }} />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 rounded w-3/4 animate-pulse" style={{ backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)' }} />
                        <div className="h-3 rounded w-1/2 animate-pulse" style={{ backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }} />
                      </div>
                    </div>
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex items-center gap-3 px-2 py-3">
                        <div className="h-9 w-9 shrink-0 rounded-lg animate-pulse" style={{ backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)' }} />
                        <div className="flex-1 space-y-2">
                          <div className="h-4 rounded w-2/3 animate-pulse" style={{ backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)' }} />
                          <div className="h-3 rounded w-1/2 animate-pulse" style={{ backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {(!searchQuery || searchQuery.length < 2) && !isSearching && (
                  <div className={`px-4 py-8 text-center border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Type at least 2 characters to search
                    </p>
                    <p className={`mt-1 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      Properties, tenants, maintenance, and more
                    </p>
                  </div>
                )}
                
                {searchQuery && searchQuery.length >= 2 && searchResults.length === 0 && !isSearching && (
                  <div className={`px-4 py-10 text-center border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <FaSearch className={`mx-auto h-8 w-8 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} aria-hidden="true" />
                    <p className={`mt-3 text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                      No results found
                    </p>
                    <p className={`mt-1 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      No results for &ldquo;{searchQuery}&rdquo;. Try a different term.
                    </p>
                  </div>
                )}
              </Combobox>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TopBar;
